/**
 * QUEST PASTELS — the quest's color identity, chosen in the admin (Nick, 2026-09-01:
 * "let us just set the pastel color from the admin … give us a bunch of pastel colors
 * we can choose from that will all look good").
 *
 * Same registry pattern as `quest-types`: the DB column is plain TEXT and THIS list is
 * the authority, so adding a color is one entry here — no migration, no enum to alter.
 * The resolver validates against `QUEST_PALETTE_VALUES` at the boundary.
 *
 * Every entry is deliberately PALE — the band carries dark ink, a green progress fill,
 * and a green "do this next" card on top of it, so a saturated ground would fight all
 * three. Each color is one hue rendered three ways: `background` (the band's ground,
 * a soft diagonal wash), `borderColor` (the same hue, one step darker so the card still
 * has an edge), and `tile` (the icon chip, a step stronger so the emoji sits on color).
 * Chosen this way, ANY selection stays legible — that is the promise the admin picker
 * makes by only offering these.
 *
 * A quest with no color set falls back to a hash of its slug (`questPastelFor`), so the
 * quests that predate this picker still look distinct instead of all going gray.
 */

export type QuestPaletteEntry = {
  value: string;
  label: string;
  /** The band's ground. */
  background: string;
  /** The band's edge — the same hue, one step darker. */
  borderColor: string;
  /** The icon chip behind the quest's emoji. */
  tile: string;
  /** A flat swatch for the admin picker. */
  swatch: string;
};

function pastel(value: string, label: string, hue: number, saturation: number): QuestPaletteEntry {
  return {
    value,
    label,
    background: `linear-gradient(135deg, hsl(${hue} ${saturation}% 96%), hsl(${hue} ${saturation}% 99%))`,
    borderColor: `hsl(${hue} ${Math.round(saturation * 0.7)}% 86%)`,
    tile: `linear-gradient(135deg, hsl(${hue} ${saturation}% 92%), hsl(${hue} ${saturation}% 97%))`,
    swatch: `hsl(${hue} ${saturation}% 92%)`,
  };
}

/**
 * The neutral pastel. Named because it doubles as the fallback in `questPaletteFor` —
 * the modulo index there is always in bounds, but `noUncheckedIndexedAccess` cannot
 * know the list is non-empty.
 */
const NEUTRAL = pastel('cloud', 'Cloud', 220, 16);

export const QUEST_PALETTE: QuestPaletteEntry[] = [
  pastel('blush', 'Blush', 350, 70),
  pastel('rose', 'Rose', 335, 62),
  pastel('lilac', 'Lilac', 288, 60),
  pastel('periwinkle', 'Periwinkle', 250, 66),
  pastel('sky', 'Sky', 205, 78),
  pastel('seafoam', 'Seafoam', 176, 55),
  pastel('mint', 'Mint', 152, 52),
  pastel('sage', 'Sage', 120, 34),
  pastel('butter', 'Butter', 48, 82),
  pastel('sand', 'Sand', 33, 62),
  pastel('peach', 'Peach', 20, 82),
  NEUTRAL,
];

export const QUEST_PALETTE_VALUES = QUEST_PALETTE.map((entry) => entry.value);

/**
 * The quest's colors: its chosen palette entry, or — when none is set — a stable one
 * hashed from its slug, so the same quest always wears the same color everywhere.
 */
export function questPaletteFor(slug: string, color?: string | null): QuestPaletteEntry {
  if (color) {
    const chosen = QUEST_PALETTE.find((entry) => entry.value === color);
    if (chosen) return chosen;
  }
  let hash = 0;
  for (let index = 0; index < slug.length; index += 1) hash = (hash * 31 + slug.charCodeAt(index)) >>> 0;
  return QUEST_PALETTE[hash % QUEST_PALETTE.length] ?? NEUTRAL;
}
